import React from 'react';
import { motion } from 'framer-motion';
import { ArrowRight, Activity, Shield, Database } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useAnalytics } from '../../hooks/useAnalytics';

const Hero = () => {
    const navigate = useNavigate();
    const { logEvent, events } = useAnalytics();

    const videoRef = React.useRef(null);

    React.useEffect(() => {
        if (videoRef.current) {
            videoRef.current.playbackRate = 0.5;
        }
    }, []);

    const handleEnterPortal = () => {
        logEvent(events.CONVERSION, {
            conversion_type: 'enter_portal',
            location: 'hero'
        });
        navigate('/app');
    };

    const handleReadManifesto = () => {
        logEvent(events.ENGAGEMENT, {
            engagement_type: 'read_manifesto',
            location: 'hero'
        });
        document.getElementById('manifesto')?.scrollIntoView({ behavior: 'smooth' });
    };

    return (
        <section className="relative min-h-screen flex items-center justify-center overflow-hidden bg-background border-b border-primary/20 pt-32 md:pt-20">
            {/* Background Layer */}
            <div className="absolute inset-0 z-0 bg-zinc-900">
                {/* 1. Mobile/Fallback Image - Loads instantly, lightweight (~200KB) */}
                <img
                    src="/assets/hero-iztacalco.jpg"
                    alt="Background"
                    className="absolute inset-0 w-full h-full object-cover opacity-50 md:opacity-0 transition-opacity duration-700"
                />

                {/* 2. Desktop Video - Only loads/plays on larger screens (md+) */}
                <video
                    ref={videoRef}
                    autoPlay
                    loop
                    muted
                    playsInline
                    poster="/assets/hero-iztacalco.jpg"
                    className="absolute inset-0 w-full h-full object-cover hidden md:block fade-in"
                >
                    {/* Media query prevents download on mobile */}
                    <source src="/assets/iztacalcoflow.mp4" type="video/mp4" media="(min-width: 768px)" />
                </video>

                {/* 3. Global Overlay - Ensures text legibility on both mobile & desktop */}
                <div className="absolute inset-0 bg-black/70"></div>
            </div>

            <div className="container mx-auto px-6 relative z-10 flex flex-col items-center justify-center text-center">

                {/* Text Content */}
                <motion.div
                    initial={{ opacity: 0, y: 30 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.8, ease: "easeOut" }}
                    className="max-w-4xl"
                >
                    <div className="flex items-center justify-center gap-2 mb-6">
                        <span className="px-3 py-1 border border-primary text-primary text-xs font-mono uppercase tracking-widest rounded bg-primary/10">
                            Think Tank
                        </span>
                        <span className="text-text-muted text-xs font-mono">v2.2 PHOENIX (Verified)</span>
                    </div>

                    <h1 className="text-5xl lg:text-7xl font-display font-bold text-white leading-tight mb-6">
                        La Revolución Comienza en el <span className="text-primary">Barrio</span>
                    </h1>

                    <p className="text-xl text-text-muted font-body mb-8 max-w-lg mx-auto leading-relaxed">
                        Operamos como un laboratorio de políticas públicas. Utilizamos <strong className="text-white">Ciencia de Datos, IA e IoT</strong> para diagnosticar y resolver fallos sistémicos. No somos una ONG desconectada; somos un vehículo para la <strong className="text-primary">acción ciudadana</strong>.
                    </p>

                    <div className="flex flex-col sm:flex-row justify-center gap-4">
                        <button onClick={handleEnterPortal} className="group flex items-center justify-center gap-3 bg-primary text-background px-8 py-4 rounded font-mono font-bold text-lg hover:shadow-[0_0_20px_rgba(0,255,65,0.4)] transition-all">
                            ENTRAR AL PORTAL
                            <ArrowRight className="group-hover:translate-x-1 transition-transform" />
                        </button>
                        <button
                            onClick={handleReadManifesto}
                            className="flex items-center justify-center gap-3 px-8 py-4 border border-zinc-700 text-white rounded font-mono hover:bg-zinc-800 transition-all"
                        >
                            LEER MANIFIESTO
                        </button>
                    </div>
                </motion.div>

            </div>

            {/* Scroll Indicator */}
            <motion.div
                animate={{ y: [0, 10, 0] }}
                transition={{ repeat: Infinity, duration: 2 }}
                className="absolute bottom-8 left-1/2 -translate-x-1/2 text-zinc-500"
            >
                <div className="w-[1px] h-12 bg-gradient-to-b from-transparent via-primary to-transparent"></div>
            </motion.div>
        </section>
    );
};

export default Hero;
